// Izračun izvedenih boja za online recepciju na temelju 2 boje koje hotel odabere
// (pozadina + akcentna boja). Ako ni jedna nije postavljena, vraća se null i stranica
// koristi postojeće default (light) CSS vrijednosti - ništa se ne mijenja.

const DEFAULT_BACKGROUND = '#f8f9fa';
const DEFAULT_ACCENT = '#3b5bdb';
const DEFAULT_SURFACE = '#ffffff';
const DEFAULT_TEXT = '#1a1a2e';

const hexToRgb = (hex: string): [number, number, number] => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const value = parseInt(full, 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const channelLuminance = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

const relativeLuminance = (hex: string): number => {
    const [r, g, b] = hexToRgb(hex);
    return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
};

const contrastRatio = (hexA: string, hexB: string): number => {
    const l1 = relativeLuminance(hexA);
    const l2 = relativeLuminance(hexB);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
};

// Bijela ili gotovo-crna, ovisno koja od te dvije ima bolji kontrast na danoj pozadini
const pickReadableTextColor = (bgHex: string): string => {
    const white = '#ffffff';
    const nearBlack = '#17120e';
    return contrastRatio(bgHex, white) >= contrastRatio(bgHex, nearBlack) ? white : nearBlack;
};

const mixToward = (hex: string, target: [number, number, number], ratio: number): string => {
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(
        r + (target[0] - r) * ratio,
        g + (target[1] - g) * ratio,
        b + (target[2] - b) * ratio
    );
};

const lighten = (hex: string, ratio: number) => mixToward(hex, [255, 255, 255], ratio);
const darken = (hex: string, ratio: number) => mixToward(hex, [0, 0, 0], ratio);

const hexToRgbTriplet = (hex: string): string => hexToRgb(hex).join(', ');

export interface HbThemeVars {
    [key: string]: string;
}

// Boja se tretira kao "postavljena" samo ako je stvarno drugačija od defaulta -
// ako netko otvori color picker i spremi ga bez promjene (ostane na default swatchu),
// to se ponaša isto kao da uopće nije postavljeno.
const isCustomized = (value: string | null | undefined, defaultValue: string): boolean =>
    !!value && value.toLowerCase() !== defaultValue.toLowerCase();

export const buildHbTheme = (
    backgroundColor?: string | null,
    accentColor?: string | null
): HbThemeVars | null => {
    const bgCustomized = isCustomized(backgroundColor, DEFAULT_BACKGROUND);
    const accentCustomized = isCustomized(accentColor, DEFAULT_ACCENT);

    if (!bgCustomized && !accentCustomized) return null;

    const theme: HbThemeVars = {};

    // bgIsDark se koristi i izvan ovog bloka (za smjer prigušenih akcent nijansi) - ako pozadina
    // nije postavljena, ponašamo se kao da je default (svijetla), isto kao kad ovaj kod uopće ne postoji.
    let bgIsDark = false;

    // Sve što ovisi SAMO o pozadini diramo samo ako je pozadina stvarno prilagođena -
    // inače ostaju CSS default vrijednosti (originalna hijerarhija teksta ostane netaknuta).
    if (bgCustomized) {
        const bg = backgroundColor as string;
        const text = pickReadableTextColor(bg);
        bgIsDark = text === '#ffffff';

        // Kartice/rubovi uvijek posvjetljuju pozadinu (prema bijeloj) - tako se ponaša i default light
        // (bg #f8f9fa -> surface #ffffff) i ranije ručno napravljen dark (bg #17120e -> surface #241c16).
        // Ovo NE ovisi o boji teksta - to je zaseban vizualni odabir (kartica uvijek "iskače" svjetlije od pozadine).
        theme['--hb-bg'] = bg;
        theme['--hb-surface'] = lighten(bg, 0.08);
        theme['--hb-header-bg'] = theme['--hb-surface'];
        theme['--hb-search-bg'] = theme['--hb-surface'];
        theme['--hb-border'] = lighten(bg, 0.18);
        theme['--hb-border-strong'] = lighten(bg, 0.26);
        theme['--hb-text'] = text;
        theme['--hb-text-2'] = text;
        theme['--hb-text-3'] = text;
        // text-4 se koristi samo za placeholder/hint/staru cijenu - namjerno prigušeniji od
        // ostatka teksta, inače placeholder u inputu izgleda kao da je polje već popunjeno.
        theme['--hb-text-4'] = `rgba(${text === '#ffffff' ? '255, 255, 255' : '23, 18, 14'}, 0.45)`;
        theme['--hb-accent-text'] = text;
    }

    // Sve što ovisi o akcentnoj boji diramo samo ako je akcent stvarno prilagođen.
    if (accentCustomized) {
        const accent = accentColor as string;

        // Prigušene/tint varijante akcenta trebaju ići prema TONU POZADINE (da se uklope s temom),
        // ne prema boji teksta - inače na tamnoj pozadini ispadnu svijetle/isprane i bijeli tekst na njima postane nečitljiv.
        // Ako pozadina nije prilagođena, tretiramo je kao default (svijetlu).
        const towardBgTone: [number, number, number] = bgIsDark ? [0, 0, 0] : [255, 255, 255];
        const accentContrast = pickReadableTextColor(accent);
        const accentRgb = hexToRgbTriplet(accent);

        theme['--hb-accent'] = accent;
        theme['--hb-accent-hover'] = darken(accent, 0.12);
        theme['--hb-accent-light'] = mixToward(accent, towardBgTone, 0.85);
        theme['--hb-accent-border'] = mixToward(accent, towardBgTone, 0.6);
        theme['--hb-accent-contrast'] = accentContrast;
        theme['--hb-accent-contrast-rgb'] = hexToRgbTriplet(accentContrast);
        theme['--hb-accent-rgb'] = accentRgb;
        theme['--hb-rate-alt-bg'] = `rgba(${accentRgb}, 0.08)`;
    }

    // Za elemente koji žele akcentnu boju kao tekst (npr. naziv sobe) na pozadini kartice -
    // ako akcent (postavljen ili default) nema dovoljno kontrasta na trenutnoj (postavljenoj ili default)
    // pozadini kartice, koristi običnu boju teksta umjesto akcenta, da naslov ne postane teško čitljiv.
    const effectiveAccent = accentCustomized ? (accentColor as string) : DEFAULT_ACCENT;
    const effectiveSurface = bgCustomized ? theme['--hb-surface'] : DEFAULT_SURFACE;
    const effectiveText = bgCustomized ? theme['--hb-text'] : DEFAULT_TEXT;
    theme['--hb-heading-accent'] =
        contrastRatio(effectiveAccent, effectiveSurface) >= 3 ? effectiveAccent : effectiveText;

    return theme;
};

// Treba li logo (crna SVG grafika) biti invertiran u bijelo da bude vidljiv na izračunatoj pozadini.
export const isDarkHbTheme = (theme: HbThemeVars | null): boolean => theme?.['--hb-text'] === '#ffffff';
